import { patchModal, popModal, pushModal } from 'components/modal/store'
import { defineComponent, ref, watch } from 'vue'
import FormWizard from 'components/form-wizard'
import { sleep } from '@/utils/time'
import { computed } from 'vue'
import { useImportAssets } from '../../hooks/useAssetForm/hooks/useImportAssets'
import { clamp } from 'math'
import type { IntegrationProvider, IProviderImport } from 'findef'
import { ProviderImportTable } from './components/providerTable'
import { FinsquidError } from '@/modules/providerImport/components/finsquid-error'
import { useResponsive } from '~/composables/useResponsive'

type ProviderImport = Omit<IProviderImport, 'provider'> & { provider: IntegrationProvider };

const ModalBody = defineComponent<{
  handleCancel: () => any
  handleSave: () => any
  onFinsquidComplete: () => any
  providerImport?: ProviderImport,
  hideBackButtonIndex?: number,
  title?: string
  breadcrumbs?: boolean
}>(
  (props) => {
    const responsive = useResponsive();
    const handleGoBack = (_index: number) => {
      queueMicrotask(() => {
        viewIndex.value = 0

        patchModal((s) => {
          s.hideTopBar = false
          return s
        })
      })
    }

    const importAssets = useImportAssets({
      breadcrumbs: props.breadcrumbs ?? false,
      selectProviderComponent: (props) => <div class={[
        "w-full h-full",
        responsive.isSmall.value ? "px-3" : "px-12"
      ]}>
        <ProviderImportTable handleSelectImport={props.handleSelectProviderImport}/>
      </div>,
      //step: 1,
      onCancelOverride: props.handleCancel,
      providerImport: props.providerImport,
      onSave: props.handleSave,
      hideBackButtonOnIndex: props.hideBackButtonIndex ?? 0,
      onLastGoBack: (index) => {
        handleGoBack(index)
      },
      title: props.title
    })
    const viewIndex = ref<number>(0)


    watch(importAssets.errors, (errors) => {
      if (errors.length > 0) {
        patchModal((s) => {
          s.hideTopBar = false;
          return s;
        })
      }
    })

    const views = [
      () => <FormWizard handler={importAssets.wizard} />
    ]

    const currentView = computed(() => views[clamp(viewIndex.value, 0, views.length - 1)])

    return () => {
      return (
        <div
          class="pretty-scrollbar"
          style={{
            width: '100%',
            height: '100%',
            maxWidth: '100vw',
            maxHeight: '100svh',
            overflow: 'auto'
          }}
        >
          {importAssets.errors.value.length > 0 ? (
            <FinsquidError importAssets={importAssets.providerImportHook}/>
          ) : currentView.value()}
        </div>
      )
    }
  },
  { props: ['handleCancel', 'handleSave', 'onFinsquidComplete', 'providerImport', 'hideBackButtonIndex', 'title'] }
)

export const pushImportAssetsModal = async (
  props: {
    onSave?: () => any,
    providerImport?: ProviderImport,
    hideBackButtonIndex?: number,
    title?: string,
    breadcrumbs?: boolean
  } = {}
) => {

  const handleCancel = () => {
    popModal()
  }

  const handleFinsquidComplete = async () => {
    await sleep(300)
    await popModal() // pop finsquid modal
    await popModal() // pop this modal
    if (props.onSave) {
      await props.onSave()
    }
  }

  const handleSave = async () => {
    if (props.onSave) {
      await props.onSave()
    }
    popModal()
  }

  return await pushModal({
    hideActions: true,
    hideTopBar: true,
    disableDefaultContentStyles: true,
    title: props.title || 'Connect Account',
    body: () => {
      return (
        <ModalBody
          onFinsquidComplete={handleFinsquidComplete}
          handleCancel={handleCancel}
          handleSave={handleSave}
          providerImport={props.providerImport}
          hideBackButtonIndex={props.hideBackButtonIndex}
          title={props.title}
          breadcrumbs={props.breadcrumbs}
        />
      )
    },
    style: {
      minWidth: '640px',
      maxWidth: '1100px',
      minHeight: '650px',
      maxHeight: '1200px',
      height: '70%',
      padding: '0px'
    },
    fullscreen: false
  })
}
